import React, { useEffect, useState } from "react";
import styles from "../shipments-table.module.css";
import { Table, TableColumn, TableState } from "@/components/Table/Table";
import { FBA_API_VERSION, InboundBatch, TransportType, ListShipmentsSort } from "@boxem/core-api-schema";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import Skeleton from "@/components/Skeleton/Skeleton";
import Button from "@/components/forms/Button/Button";
import { getClosedShipments } from "@/api/fulfillment";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { useLocation } from "wouter";
import { useShipmentContext } from "@/contexts/ShipmentContext";
import ShipmentsZeroState from "@/components/ShipmentsZeroState/ShipmentsZeroState";
import { LocalStorageKeys, getLocalStorageItem } from "@/utils/localStorageUtils";
import { formatValue } from "@/utils/formatUtils";
import { formatISODate } from "@/utils/dateUtils";
import Pagination from "@/components/Pagination/Pagination";
import ShipmentStatusBadge from "@/components/ShipmentStatusBadge/ShipmentStatusBadge";
import SearchInput from "@/components/SearchInput";
import { API } from "@/lib/api";

interface ClosedShipmentsTableState {
  sortedColumnId: ListShipmentsSort | undefined;
  sortDirection: 'asc' | 'desc' | undefined;
  page: number;
  limit: number;
}

type ClosedShipmentTableRecord = {
  id: number;
  shipmentId: string;
  batchId: number|null;
  name: string;
  batchName?: string;
  batchVersion?: string;
  type: TransportType;
  status?: string;
  percentageReceived?: string;
  totalUnits: number;
  totalSkus: number;
  createdAt: Date;
  fromSync: boolean;
};

type ClosedShipmentsTableProps = {};

const ClosedShipmentsTable = ({}: ClosedShipmentsTableProps) => {
  const { createShipment } = useShipmentContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const [location] = useLocation();
  const [displayingShipments, setDisplayingShipments] = useState<ClosedShipmentTableRecord[]>([]);
  const [searchInputText, setSearchInputText] = useState<string>("");
  const [shipmentsLoading, setShipmentsLoading] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState(0);
  const tableName = "closedShipmentsTable";
  const [tableState, setTableState] = useState<ClosedShipmentsTableState>(() => {
    const tableProfile: ClosedShipmentsTableState = getLocalStorageItem(`${LocalStorageKeys.tableStatePrefix}${tableName}`);

    return {
      sortedColumnId: tableProfile?.sortedColumnId ?? undefined,
      sortDirection: tableProfile?.sortDirection ?? undefined,
      page: 1,
      limit: 10,
    };
  });

  const handleSortChange = async (columnId: ListShipmentsSort, direction: 'asc' | 'desc') => {
    if(!Object.keys(ListShipmentsSort).includes(columnId)) {
      console.error(
        "List Shipments Sort doesn't contain column ",
        columnId,
        "!!! So we probably didn't implement it in backend either. Invalid values should fall through as a default createdAt, but we should fix this"
      );
    }
    setTableState({
      ...tableState,
      sortedColumnId: columnId,
      sortDirection: direction,
      page: 1,
    });
  };

  const handlePageSizeChange = async (newPageSize: number) => {
    setTableState({
      ...tableState,
      page: 1,
      limit: newPageSize,
    });
  };

  const handlePageChange = async (newPage: number) => {
    setTableState({
      ...tableState,
      page: newPage,
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchInputText(value);
    if (value && tableState.page !== 1) {
      setTableState({
        ...tableState,
        page: 1,
      });
    }
  };

  const onClearSearch = () => {
    setSearchInputText("");
  };

  const columns: TableColumn<ClosedShipmentTableRecord>[] = [
    {
      title: 'Name',
      dataKey: 'name',
      id: 'name',
      sortable: true,
      width: '15rem',
      alignment: 'left',
      renderRowCell: (row) => (
        <div className={styles.shipmentItem}>
          <Tooltip
            title={row.getCellData('batchName') ?? row.getCellData('name')}
            placement='right'
          >
            {row.getCellData('name')}
          </Tooltip>
        </div>
      ),
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-long" />
        </div>
      )
    },
    {
      title: 'Type',
      dataKey: 'type',
      id: 'type',
      sortable: true,
      width: '6rem',
      alignment: 'left',
      renderRowCell: (row) => (
        <div className={styles.shipmentItem}>
          {row.getCellData('type')}
        </div>
      ),
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-short" />
        </div>
      )
    },
    {
      title: 'Status',
      dataKey: 'status',
      id: 'status',
      sortable: false,
      width: '8rem',
      alignment: 'left',
      renderRowCell: (row) => (
        <div className={styles.shipmentItem}>
          <ShipmentStatusBadge status={row.getCellData('status')} />
        </div>
      ),
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-medium" />
        </div>
      )
    },
    {
      title: '% of units received',
      dataKey: 'percentOfUnits',
      id: 'percentOfUnits',
      sortable: false,
      width: '11rem',
      alignment: 'left',
      renderRowCell: (row) => {
        let percentageReceived = parseInt(row.getCellData('percentageReceived'));
        if (percentageReceived > 100) percentageReceived = 100; //Ensure that the max is always 100%.

        return (
          <div className={styles.shipmentItem}>
            <div className={styles.percentageReceivedContainer}>
              <div className={`${percentageReceived > 0 ? styles.percentageReceivedText : ""}`}>{percentageReceived}%</div>
              {percentageReceived > 0 && (
                <div
                  className={styles.percentageReceivedLoader}
                  style={{
                    width: `${percentageReceived}px`
                  }}
                />
              )}
            </div>
          </div>
        );
      },
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-medium" />
        </div>
      )
    },
    {
      title: 'Units',
      dataKey: 'totalUnits',
      id: 'totalUnits',
      sortable: true,
      width: '5rem',
      alignment: 'left',
      renderRowCell: (row) => formatValue(row.getCellData('totalUnits'), 'number'),
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-short" />
        </div>
      )
    },
    {
      title: 'SKUs',
      dataKey: 'totalSkus',
      id: 'totalSkus',
      sortable: true,
      width: '5rem',
      alignment: 'left',
      renderRowCell: (row) => formatValue(row.getCellData('totalSkus'), 'number'),
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-short" />
        </div>
      )
    },
    {
      title: 'Created At',
      dataKey: 'createdAt',
      id: 'createdAt',
      sortable: true,
      width: '11.5rem',
      alignment: 'left',
      renderRowCell: (row) => (
        <div className={styles.shipmentItem}>
          {!!row.getCellData('fromSync') ? (
            <div className={styles.externalBadge}>
              External Shipment
            </div>
          ) : (
            <>
              {formatISODate(row.getCellData('createdAt'))}
            </>
          )}
        </div>
      ),
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-short" />
        </div>
      )
    },
    {
      title: '',
      dataKey: undefined,
      id: 'actions',
      sortable: false,
      pinned: 'right',
      alignment: 'right',
      width: '6rem',
      renderRowCell: (row) => {
        const shipment: ClosedShipmentTableRecord = row.rowData;
        return (
        <div className={styles.shipmentItem}>
          <a href={
            (shipment.fromSync || !shipment.batchId || shipment.batchVersion === '1')
              ? (`/shipments/external/${shipment.id}?prev=closed`)
              : (`/fba/shipments/${shipment.batchId}?shipment=${shipment.shipmentId}&return=${encodeURIComponent(location)}`)
          }>
          <Button type="secondary">View</Button>
          </a>
        </div>
      )},
    },
  ];

  const handleGetClosedShipments = async () => {
    const ascending: boolean = tableState.sortDirection == "asc" ? true : false;
    return await getClosedShipments(tableState.page - 1, tableState.limit, searchInputText, tableState.sortedColumnId, ascending)
      .then(async res => {
        setTotalCount(res.count);
        const newClosedShipments: ClosedShipmentTableRecord[] = [];

        // @NOTE(michaelp): Duplicated code in ShippedShipments, search codebase for (NEMOZOID)
        let batchPromises: Promise<InboundBatch>[] = [];
        for (let i = 0; i < res.shipments.length; i++) {
          const shipment = res.shipments[i];
          if (shipment.batchId) {
            batchPromises[i] = API.get<InboundBatch>(`${FBA_API_VERSION}/fba/batches/${shipment.batchId}`);
          } else batchPromises[i] = null;
        }
        const fetchedBatches = await Promise.all(batchPromises);
        for (let i = 0; i < res.shipments.length; i++) {
          const shipment = res.shipments[i];
          const batch: InboundBatch|undefined = fetchedBatches[i];

          let totalUnits = 0;
          let totalUnitsReceived = 0;
          for (const item of shipment.items) {
            totalUnits += item.quantity;
            totalUnitsReceived += item.quantityReceived;
          }

          //console.log(shipment.id + ": JS counted units " + totalUnits + ", JS counted skus: " + shipment.items.length, "SQL counted skus: " + shipment.total_skus + ", SQL counted quantity: " + shipment.total_quantity, shipment);
          newClosedShipments.push({
            id: shipment.id,
            batchId: (shipment.batchId >= 0) ? shipment.batchId : null,
            shipmentId: shipment.shipmentId,
            name: shipment.shipmentName,
            batchName: batch?.name,
            batchVersion: batch?.version,
            type: shipment.transportType,
            status: shipment.status,
            totalSkus: shipment.items.length,
            totalUnits,
            percentageReceived: String(Math.round((totalUnitsReceived / totalUnits) * 100) || 0),
            createdAt: shipment.createdAt,
            fromSync: shipment.fromSync,
          })
        }

        setDisplayingShipments(newClosedShipments);
      });
  };

  useEffect(() => {
    setShipmentsLoading(true);
    handleGetClosedShipments().catch(err => handleErrorResponseGenerically(err)).finally(() => setShipmentsLoading(false));
  }, [
    searchInputText,
    tableState.page,
    tableState.limit,
    tableState.sortDirection,
    tableState.sortedColumnId,
  ]);

  return (
    <>
      <Table
        data={displayingShipments}
        columns={columns}
        tableName={tableName}
        className={styles.table}
        isLoading={shipmentsLoading}
        skeletonRows={tableState.limit}
        sortDirection={tableState.sortDirection}
        sortedColumnId={tableState.sortedColumnId}
        onSort={handleSortChange}
        showSort
        header={
          <>
            <div className={styles.searchInputContainer}>
              <SearchInput
                searchTerm={searchInputText}
                onDebounceChange={handleSearchChange}
                placeholder="Search by shipment name or ID"
                className={styles.searchInput}
              />
            </div>
          </>
        }
        zeroState={
          (!shipmentsLoading && displayingShipments?.length === 0 && searchInputText === "") ? (
            <ShipmentsZeroState
              type="default"
              onCreateShipment={createShipment}
            />
          ) : undefined
        }
        noResultsState={
          (!shipmentsLoading && displayingShipments?.length === 0 && searchInputText !== "") ? (
            <ShipmentsZeroState
              type="search"
              onClearSearch={onClearSearch}
            />
          ) : undefined
        }
        footer={
          <Pagination
            total={totalCount || 0}
            pageSize={tableState.limit}
            onChangePageSize={handlePageSizeChange}
            page={tableState.page}
            onChangePage={handlePageChange}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        }
      />
    </>
  );
};

export default ClosedShipmentsTable;